<i18n>
{
  "fr": {
    "indicator-general-status": "Statut",
    "indicator-general-status-placeholder": "Assigner un statut",
    "indicator-general-owner": "Responsable",
    "indicator-general-owner-placeholder": "Assigner un responsable",
    "indicator-general-targetDate": "Date visée",
    "indicator-general-targetDate-placeholder": "Sélectionner une date visée",
    "indicator-general-difficulty": "Difficulté",
    "indicator-general-difficulty-placeholder": "Attribuer un niveau de difficulté"
  },
  "en": {
    "indicator-general-status": "Status",
    "indicator-general-status-placeholder": "Set a status",
    "indicator-general-owner": "Assignee",
    "indicator-general-owner-placeholder": "Assign a assignee",
    "indicator-general-targetDate": "Target date",
    "indicator-general-targetDate-placeholder": "Select a target date",
    "indicator-general-difficulty": "Difficulty",
    "indicator-general-difficulty-placeholder": "Set a difficulty level"
  }
}
</i18n>

<template>
  <ui-box v-if="indicatorModel" class="has-background-grey-lightest py-24px">
    <div class="columns is-multiline">
      <div class="column is-6-tablet is-3-fullhd">
        <!-- Status -->
        <s-field :label="$t('indicator-general-status')" custom-class="is-size-7 is-uppercase">
          <b-select
            icon="check-circle"
            expanded
            :placeholder="$t('indicator-general-status-placeholder')"
            v-model="indicatorModel.status"
            @input="updateIndicatorStatus"
            :disabled="readOnly"
          >
            <option v-for="i in indicatorStatuses" :key="i.value" :value="i.value">{{ i.text }}</option>
          </b-select>
        </s-field>
      </div>

      <div class="column is-12" v-if="isNotApplicable && $breakpoint.mobile" key="justification">
        <justification-picker
          v-if="indicatorModel && business"
          :justification="indicatorModel.justification"
          :reason="indicatorModel.justificationOther"
          :indicatorGraphType="indicator.__typename"
          :businessCategory="business.category"
          :readOnly="readOnly"
          @justification-changed="updateIndicatorJustification"
          @reason-changed="updateIndicatorJustificationOther"
        />
      </div>

      <div class="column is-6-tablet is-3-fullhd">
        <!-- Responsable -->
        <s-field :label="$t('indicator-general-owner')" custom-class="is-size-7 is-uppercase">
          <b-select
            icon="user"
            expanded
            :placeholder="$t('indicator-general-owner-placeholder')"
            v-model="indicatorModel.ownerId"
            :disabled="readOnly"
            @input="updateIndicatorOwner"
          >
            <option value></option>
            <option v-for="o in indicator.availableOwners" :key="o.id" :value="o.id">
              {{ o.profile.firstName }} {{ o.profile.lastName }}
            </option>
          </b-select>
        </s-field>
      </div>

      <div class="column is-12" v-if="isNotApplicable && $breakpoint.tabletAndUp && !$breakpoint.fullhd" key="justification">
        <justification-picker
          v-if="indicatorModel && business"
          :justification="indicatorModel.justification"
          :reason="indicatorModel.justificationOther"
          :indicatorGraphType="indicator.__typename"
          :businessCategory="business.category"
          :readOnly="readOnly"
          @justification-changed="updateIndicatorJustification"
          @reason-changed="updateIndicatorJustificationOther"
        />
      </div>

      <div class="column is-6-tablet is-3-fullhd">
        <!-- Date visée -->
        <s-field :label="$t('indicator-general-targetDate')" custom-class="is-size-7 is-uppercase">
          <s-locale-date
            icon="calendar-day"
            :placeholder="$t('indicator-general-targetDate-placeholder')"
            v-model="indicatorModel.targetDate"
            :readOnly="readOnly"
            @input="updateIndicatorTargetDate"
            nullable
          ></s-locale-date>
        </s-field>
      </div>
      <div class="column is-6-tablet is-3-fullhd">
        <!-- Dificulté -->
        <s-field :label="$t('indicator-general-difficulty')" custom-class="is-size-7 is-uppercase">
          <b-select
            icon="signal"
            :placeholder="$t('indicator-general-difficulty-placeholder')"
            expanded
            v-model="indicatorModel.difficulty"
            :disabled="readOnly"
            @input="updateIndicatorDifficulty"
          >
            <option v-for="d in indicatorDifficulties" :key="d.value" :value="d.value">{{ d.text }}</option>
          </b-select>
        </s-field>
      </div>

      <div class="column is-6" v-if="isNotApplicable && $breakpoint.fullhd" key="justification">
        <justification-picker
          v-if="indicatorModel && business"
          :justification="indicatorModel.justification"
          :reason="indicatorModel.justificationOther"
          :indicatorGraphType="indicator.__typename"
          :businessCategory="business.category"
          :readOnly="readOnly"
          @justification-changed="updateIndicatorJustification"
          @reason-changed="updateIndicatorJustificationOther"
        />
      </div>
    </div>
  </ui-box>
</template>

<script>
import {
  INDICATOR_STATUS_TODO,
  INDICATOR_STATUS_IN_PROGRESS,
  INDICATOR_STATUS_DONE,
  INDICATOR_STATUS_NOT_APPLICABLE,
  INDICATOR_DIFFICULTY_UNSET,
  INDICATOR_DIFFICULTY_EASY,
  INDICATOR_DIFFICULTY_MEDIUM,
  INDICATOR_DIFFICULTY_HARD
} from '@/consts/enums'

import BusinessService from '@/services/business'
import EnumHelper from '@/helpers/enums'
import i18n from '@/helpers/i18n'

import SField from '@/components/form/field'
import SLocaleDate from '@/components/form/locale-date'
import UiBox from '@/components/ui/box'
import JustificationPicker from '@/components/indicator/justification-picker'
import Breakpoint from '@/mixins/breakpoints'

export default {
  mixins: [Breakpoint],
  components: { SField, SLocaleDate, UiBox, JustificationPicker },
  props: {
    indicator: Object,
    readOnly: Boolean
  },
  data() {
    return {
      loading: false,
      indicatorModel: this.getIndicatorModel(this.indicator)
    }
  },
  computed: {
    currentBusinessId() {
      return BusinessService.getCurrentBusiness().id
    },
    isNotApplicable() {
      return this.indicatorModel.status === INDICATOR_STATUS_NOT_APPLICABLE
    },
    indicatorStatuses() {
      const statuses = [
        INDICATOR_STATUS_TODO,
        INDICATOR_STATUS_IN_PROGRESS,
        INDICATOR_STATUS_DONE,
        INDICATOR_STATUS_NOT_APPLICABLE
      ]

      return statuses.map((x) => {
        return {
          value: x,
          text: EnumHelper.formatIndicatorStatus(x)
        }
      })
    },
    indicatorDifficulties() {
      const difficulties = [INDICATOR_DIFFICULTY_EASY, INDICATOR_DIFFICULTY_MEDIUM, INDICATOR_DIFFICULTY_HARD]
      return [
        {
          value: null,
          text: ''
        },
        ...difficulties.map((x) => {
          return {
            value: x,
            text: EnumHelper.formatIndicatorDifficulty(x)
          }
        })
      ]
    }
  },
  methods: {
    getIndicatorModel(rawIndicator) {
      return {
        status: rawIndicator.status,
        justification: rawIndicator.justification,
        justificationOther: rawIndicator.justificationOther,
        ownerId: rawIndicator.owner ? rawIndicator.owner.id : null,
        targetDate: rawIndicator.targetDate ? rawIndicator.targetDate : null,
        difficulty: rawIndicator.difficulty !== INDICATOR_DIFFICULTY_UNSET ? rawIndicator.difficulty : null
      }
    },
    async updateIndicatorStatus() {
      if (this.indicatorModel.status === INDICATOR_STATUS_NOT_APPLICABLE) {
        if (this.indicatorModel.justification === null) {
          return
        }
      } else {
        this.indicatorModel.justification = null
      }

      await this.updateIndicatorData({
        status: { value: this.indicatorModel.status },
        justification: { value: this.indicatorModel.justification }
      })
    },
    async updateIndicatorJustification(newJustification) {
      this.indicatorModel.justification = newJustification

      await this.updateIndicatorData({
        status: { value: this.indicatorModel.status },
        justification: { value: this.indicatorModel.justification }
      })
    },
    async updateIndicatorJustificationOther(newReason) {
      if (newReason === this.indicator.justificationOther) return

      await this.updateIndicatorData({
        justificationOther: { value: newReason }
      })

      this.indicatorModel.justificationOther = newReason
    },
    async updateIndicatorOwner() {
      await this.updateIndicatorData({
        ownerId: { value: this.indicatorModel.ownerId ? this.indicatorModel.ownerId : null }
      })
    },
    async updateIndicatorTargetDate() {
      await this.updateIndicatorData({
        targetDate: { value: this.indicatorModel.targetDate ? this.indicatorModel.targetDate : null }
      })
    },
    async updateIndicatorDifficulty() {
      await this.updateIndicatorData({
        difficulty: {
          value: this.indicatorModel.difficulty === null ? INDICATOR_DIFFICULTY_UNSET : this.indicatorModel.difficulty
        }
      })
    },
    async updateIndicatorData(input) {
      const parameters = {
        indicatorId: this.indicator.id,
        language: i18n.contentLanguage(),
        ...input
      }

      await this.$apollo.mutate({
        mutation: this.$withFragments(this.$options.mutation.UpdateIndicator, this.$options.fragments.IndicatorEditorFields),
        variables: {
          input: parameters
        }
      })
      this.$success()
    }
  },
  apollo: {
    business: {
      query() {
        return this.$options.query.IndicatorEditorInfos
      },
      variables: {
        businessId: BusinessService.getCurrentBusiness().id
      },
      fetchPolicy: 'cache-first'
    }
  },
  watch: {
    indicator(i) {
      this.indicatorModel = this.getIndicatorModel(i)
    }
  }
}
</script>

<graphql>
query IndicatorEditorInfos($businessId: ID!) {
  business(id: $businessId) {
    id
    category
  }
}

mutation UpdateIndicator($input: UpdateIndicatorInput!) {
  updateIndicator(input: $input) {
    indicator {
      id
      language
      updatedAt

      ...IndicatorEditorFields
    }
    project {
      id
      updatedAt
      updatedBy {
        id
        profile {
          id
          firstName
          lastName
        }
      }
    }
  }
}
</graphql>
<fragments>
fragment IndicatorEditorFields on IndicatorGraphType {
  status
  justification
  justificationOther
  owner {
    id
    profile {
      id
      firstName
      lastName
    }
  }
  availableOwners {
    id
    profile {
      id
      firstName
      lastName
    }
  }
  targetDate
  difficulty
  __typename
}
</fragments>
